export const ZOCO_CHECK_PAGE_SIZE_OPTIONS = [10, 20, 50];
export const DEFAULT_ZOCO_CHECK_PAGE_SIZE = 20;

export const normalizeText = (value) => String(value ?? "").trim();
const resolveAdvanceDisplay = (value) =>
  normalizeText(value).toLowerCase() === "activo" ? "Inactivo" : "Activo";

export const parseDateValue = (value) => {
  const raw = normalizeText(value);
  if (!raw || raw === "-") {
    return null;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    const [day, month, year] = raw.split("/");
    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const date = new Date(`${raw}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDate = (value) => {
  const date = parseDateValue(value);
  if (!date) {
    return "Sin fecha";
  }

  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const isPastDate = (value) => {
  const date = parseDateValue(value);
  if (!date) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
};

export const normalizeDigits = (value) => String(value ?? "").replace(/\D/g, "");

export const normalizeWhatsappPhone = (value) => {
  const digits = normalizeDigits(value);
  if (!digits) {
    return "";
  }

  if (digits.startsWith("549")) return digits;
  if (digits.startsWith("54")) return `549${digits.slice(2)}`;
  if (digits.startsWith("0")) return `549${digits.slice(1)}`;
  return `549${digits}`;
};

export const buildWhatsAppUrl = (phone, message = "") => {
  const normalizedPhone = normalizeWhatsappPhone(phone);
  if (!normalizedPhone) {
    return "";
  }

  const text = normalizeText(message);
  return text
    ? `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(text)}`
    : `https://wa.me/${normalizedPhone}`;
};

export const buildAttemptLabel = (attempt, maxAttempts = 3) => {
  const current = Math.max(1, Number(attempt || 1));
  const max = Math.max(current, Number(maxAttempts || 3));
  return `${current} de ${max}`;
};

export const mapStateLabel = (state) => {
  const normalized = normalizeText(state).toLowerCase();
  if (!normalized) return "Sin estado";
  if (normalized.includes("pendiente_primer_contacto")) return "1er WhatsApp";
  if (normalized.includes("pendiente_primer_whatsapp")) return "1er WhatsApp";
  if (normalized.includes("esperando_segundo_whatsapp")) return "2do WhatsApp";
  if (normalized.includes("pendiente_whatsapp_2")) return "2do WhatsApp";
  if (normalized.includes("pendiente_llamada_2")) return "3er WhatsApp";
  if (normalized.includes("esperando_llamada")) return "Llamada";
  if (normalized.includes("pendiente_llamada")) return "Llamada";
  if (normalized.includes("sin_respuesta")) return "Sin respuesta";
  if (normalized.includes("contactado")) return "Contactado";
  if (normalized.includes("cerrado")) return "Cerrado";
  return normalizeText(state);
};

export const normalizeZocoCheckItem = (item = {}) => ({
  id: Number(item?.id || item?.Id || 0),
  processCode: item?.procesoCodigo || item?.ProcesoCodigo || "",
  processSlug: item?.procesoSlug || item?.ProcesoSlug || "zoco-check",
  commerceName: normalizeText(item?.comercio || item?.Comercio || item?.razonSocial || item?.RazonSocial) || "Sin comercio",
  cuit: normalizeText(item?.cuit || item?.Cuit) || "Sin CUIT",
  terminal: normalizeText(item?.numeroTerminal || item?.NumeroTerminal || item?.terminal || item?.Terminal),
  numeroComercio: normalizeText(item?.numeroComercio || item?.NumeroComercio),
  advanceActive: resolveAdvanceDisplay(item?.anticipoActivo || item?.AnticipoActivo),
  province: normalizeText(item?.provincia || item?.Provincia),
  phone: normalizeText(item?.telefono || item?.Telefono),
  priority: normalizeText(item?.prioridad || item?.Prioridad),
  responsible: normalizeText(item?.responsable || item?.Responsable) || "Sin asignar",
  attempt: Math.max(1, Number(item?.intentoActual || item?.IntentoActual || 1)),
  maxAttempts: Math.max(3, Number(item?.maximoIntentos || item?.MaximoIntentos || 3)),
  status: normalizeText(item?.estadoActual || item?.EstadoActual || item?.estadoLabel || item?.EstadoLabel),
  statusLabel: mapStateLabel(item?.estadoLabel || item?.EstadoLabel || item?.estadoActual || item?.EstadoActual),
  result: normalizeText(item?.resultadoActual || item?.ResultadoActual),
  nextContactAt: normalizeText(item?.fechaProximaAccion || item?.FechaProximaAccion),
  suggestedMessage: normalizeText(item?.mensajeSugerido || item?.MensajeSugerido),
  rowVersion: item?.versionToken || item?.VersionToken || "",
  canTake: Boolean(item?.puedeTomar),
  canAssign: Boolean(item?.puedeAsignar),
  canRelease: Boolean(item?.puedeLiberar),
  canManage: item?.puedeGestionar !== false,
  isReadOnly: Boolean(item?.esSoloLectura),
  readOnlyMessage: normalizeText(item?.mensajeSoloLectura || item?.MensajeSoloLectura),
  responsibleUserId: item?.responsableUsuarioId || item?.ResponsableUsuarioId || null,
  raw: item,
});

export const buildFilterOptions = (rows, pick) =>
  Array.from(new Set(rows.map((item) => normalizeText(pick(item))).filter(Boolean))).sort((a, b) => a.localeCompare(b, "es"));

export const filterZocoCheckRows = (rows, filters) => {
  const needle = normalizeText(filters.search).toLowerCase();

  return rows.filter((item) => {
    if (filters.priority && item.priority !== filters.priority) return false;
    if (filters.province && item.province !== filters.province) return false;
    if (filters.responsible && item.responsible !== filters.responsible) return false;
    if (filters.status && item.statusLabel !== filters.status) return false;
    if (filters.attempt && String(item.attempt) !== String(filters.attempt)) return false;
    if (!needle) return true;

    return [
      item.commerceName,
      item.cuit,
      item.numeroComercio,
      item.terminal,
      item.responsible,
      item.phone,
    ]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(needle));
  });
};

export const slicePage = (rows, page, pageSize) => rows.slice((page - 1) * pageSize, page * pageSize);

export const inferCanGenerate = (permissions) => {
  if (!permissions) {
    return true;
  }

  return Boolean(
    permissions?.puedeAdministrarPostventa ||
      permissions?.puedeGestionarPostventaGeneral ||
      permissions?.puedeGestionarPostventa
  );
};
